import styled, { css } from "styled-components";
import { type SpaceProps, space } from "styled-system";
import { type ColorType, theme } from "../theme/toyotaTheme";
import { bodyStyle, type TextStyleType } from "./Type";
import { IconWrapper } from "./IconWrapper";

export const CommonListItemStyle = css`
    margin: 0 !important;
    padding: 12px 0 !important;
    text-transform: none !important;
    color: ${theme.colors.primaryBlack};
    border-top: 1px solid ${theme.colors.grey2};
    &:last-of-type {
        border-bottom: 1px solid ${theme.colors.grey2};
    }
`;

type ListType = {
    type?: "inline" | "unstyled";
};

export const List = styled.ul.attrs<ListType>((props) => ({
    className: props.type ? `list-${props.type}` : "",
}))``;

export const ListInlineItem = styled.li.attrs(() => ({
    className: "list-inline-item",
}))``;

/**
 * List Variant: Specification list
 */
export const SpecificationList = styled.ul.attrs(() => ({
    className: "specification-list",
}))``;

export const SpecificationListItem = styled.li.attrs(() => ({
    className: "specification-item",
}))``;

/**
 * List Variant: Special links
 */
export const SpecialLinks = styled.ul.attrs(() => ({
    className: "cmp-special-links",
}))``;

export const SpecialLinksItem = styled.li.attrs(() => ({
    className: "special-links-item",
}))``;

/**
 * List Variant: Subfooter
 */
export const SubFooterList = styled.ul.attrs(() => ({
    className: "subfooter-list",
}))``;

export const SubFooterListItem = styled.li.attrs(() => ({
    className: "subfooter-item",
}))``;

/**
 * List Variant: Bullet list
 */

export const BulletList = styled.ul<SpaceProps>`
    display: block;
    margin: ${theme.space[2]}px 0;
    padding: 0;
    line-height: 3rem;
    ${space};

    + ul {
        margin-top: -${theme.space[1]}px;
    }
`;

export const BulletListItem = styled.li<{ color?: ColorType; large?: boolean; singleLine?: boolean }>`
    &,
    & > span:not(:empty) {
        font-size: ${({ large }) => (large ? 1.5 : 1.3)}rem;
        line-height: ${({ large }) => (large ? 2.4 : 2)}rem;
        color: ${({ color }) => (color ? theme.colors[color] : theme.colors.grey4)};
    }

    display: list-item;
    position: relative;
    margin-bottom: ${theme.space[3] / 2}px;
    padding-left: 20px;
    list-style-type: none;
    text-align: start;

    &::before {
        position: absolute;
        top: 0;
        left: 0;
        font-size: 3rem;
        line-height: 2rem;
        color: ${theme.colors.primaryRed};
        content: "·";
        vertical-align: sub;
    }

    ${({ singleLine }) =>
        singleLine &&
        css`
            display: flex;
        `};

    /* Update layout colorTheme bz4x */
    .bz & {
        &::before {
            color: ${theme.colors.bz4x};
        }
    }

    html[dir="rtl"] & {
        padding-left: 0;
        padding-right: 20px;

        &::before {
            left: auto;
            right: 0;
        }
    }
`;

/**
 * List variant: Check list
 */
export const CheckList = styled.ul`
    display: block;
    margin: 0;
    padding: 0;
`;

export const CheckListItem = styled.li<TextStyleType>`
    ${bodyStyle};
    display: block;
    position: relative;
    margin-bottom: 0;
    padding-left: ${theme.space[3]}px;
    font-size: 1.3rem;
    line-height: 2rem;
    color: ${theme.colors.primaryBlack};

    &:not(:last-child) {
        margin-bottom: ${theme.space[1]}px;
    }

    &::before {
        display: inline-block;
        position: absolute;
        left: 0;
        content: url("data:image/svg+xml,%3Csvg width='20' height='21' viewBox='0 0 20 21' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M15.7783 6.71268C15.937 6.86639 15.941 7.11962 15.7873 7.27829L8.39074 14.9134C8.041 15.2744 7.46335 15.279 7.10792 14.9236L4.21713 12.0328C4.06092 11.8766 4.06092 11.6233 4.21713 11.4671C4.37334 11.3109 4.62661 11.3109 4.78282 11.4671L7.67361 14.3579C7.7131 14.3974 7.77728 14.3969 7.81614 14.3568L15.2127 6.72166C15.3664 6.56299 15.6196 6.55897 15.7783 6.71268Z' fill='%23282830'/%3E%3C/svg%3E");
    }

    html[dir="rtl"] & {
        padding-left: 0;
        padding-right: ${theme.space[3]}px;
    }
`;

/**
 * List variant: Specs list
 */

export const SpecsList = styled.dl`
    display: grid;
    grid-template-columns: repeat(2, 1fr);

    dt,
    dd {
        margin-bottom: ${theme.space[1] / 2}px;
        font-size: 1.3rem;
        line-height: 2rem;
        color: ${theme.colors.primaryBlack};
    }

    dt {
        font-family: ${theme.fonts.base};
        font-weight: 400;
    }

    dd {
        font-family: ${theme.fonts.semiBold};
        text-align: end;
    }
`;

/**
 * List variant: Icon list
 */
export const IconList = styled.ul`
    display: block;
    margin: ${theme.space[2]}px 0;
    padding: 0;
    line-height: 3rem;

    & + ul {
        margin-top: -${theme.space[1]}px;
    }
`;

export const IconListItem = styled.li`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-top: ${theme.space[3]}px;

    ${IconWrapper} {
        min-width: 40px;
        margin-inline-end: ${theme.space[3]}px;
        text-align: center;
        color: ${theme.colors.primaryBlack};
    }
`;

export const IconListItemText = styled.div`
    font-family: ${theme.fonts.base};
    font-size: 1.6rem;
    line-height: 2.4rem;
    color: ${theme.colors.primaryBlack};

    strong {
        display: block;
        margin-bottom: 0;
        font-family: ${theme.fonts.regular};
    }
`;
